import { useEffect, useState, type CSSProperties } from "react";
import useSWR from "swr";
import { Table, Group, Stack, Text, Select, Badge, Alert, Menu, UnstyledButton, Tooltip, Modal, NumberInput, Button, ActionIcon } from "@mantine/core";
import { ChevronsUpDown, Pencil, Trash2 } from "lucide-react";
import { useFetch, getSubjects, getAwhSubmissions, updateAwhSubmission, createAwhSubmission, deleteAwhSubmission, AwhSubmission, AwhState, AWH_STATES, AwhRole, AWH_ROLES } from "../../../shared/api";
import { ErrorDisplay, Loading, formatDateTime, formatFullName } from "../../../shared/utils";
import { StaffSelect } from "../../submit/components/StaffSelect";
import classes from "./AwhTab.module.css";
import { FilterBar, useFilterDraft } from "../../../shared/components/FilterBar";
import { PersonName } from "../../../shared/components/PersonName";
import { Pagination } from "../../../shared/components/Pagination";

const PAGE_SIZE = 50;

const STATE_LABEL: Record<AwhState, string> = { pending: "Ожидание", blocked: "Заблокировано", success: "Начислено", failed: "Не начислено" };
const STATE_COLOR: Record<AwhState, string> = { pending: "yellow", blocked: "blue", success: "green", failed: "red" };
const ROLE_LABEL: Record<string, string> = { operator: "Оператор", editor: "Монтажёр" };
const VIA_LABEL: Record<string, string> = { lecture: "вместе с лекцией", manual: "вручную", legacy: "до учёта авторов" };

const pillVars = (color: string) => ({ "--pill-bg": `var(--mantine-color-${color}-light)`, "--pill-fg": `var(--mantine-color-${color}-light-color)`, "--pill-fg-dark": `var(--mantine-color-${color}-4)` } as CSSProperties);

/** Who made a row and how, and who last changed its amounts or people. */
function Origin({ row }: { row: AwhSubmission }) {
  const made = row.created_via ? `Создана ${VIA_LABEL[row.created_via] ?? row.created_via}${row.created_by ? `: ${row.created_by}` : ""}` : "Создана в обход сайта: нет отметки, кем и как";
  const edited = row.edited_by ? `Изменил ${row.edited_by}${row.edited_at ? `, ${formatDateTime(row.edited_at)}` : ""}` : null;
  return (
    <Tooltip multiline w={280} withArrow label={<>{made}{edited && <><br />{edited}</>}</>}>
      <Group gap={6} wrap="nowrap">
        <Text fz="sm">{formatDateTime(row.created_at)}</Text>
        {!row.created_via && <span className={classes.marker} style={pillVars("red")}>вне сайта</span>}
        {row.edited_by && <span className={classes.marker} style={pillVars("orange")}>изменена</span>}
      </Group>
    </Tooltip>
  );
}

function StateControl({ state, busy, onChange }: { state: AwhState; busy: boolean; onChange: (next: AwhState) => void }) {
  // Credited AWH is final: the server refuses to change it.
  const locked = busy || state === "success";
  const color = STATE_COLOR[state];
  return (
    <Menu withinPortal position="bottom-start" disabled={locked}>
      <Menu.Target>
        <UnstyledButton className={classes.statePill} disabled={locked} style={pillVars(color)} aria-label="Изменить статус">
          {STATE_LABEL[state]} {state !== "success" && <ChevronsUpDown size={13} />}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {AWH_STATES.map(s => (
          <Menu.Item key={s} leftSection={<Badge size="xs" circle variant="filled" color={STATE_COLOR[s]}> </Badge>} onClick={() => onChange(s)}>
            {STATE_LABEL[s]}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function SubmissionModal({ opened, row, busy, onClose, onSubmit }: { opened: boolean; row?: AwhSubmission | null; busy: boolean; onClose: () => void; onSubmit: (data: any) => void }) {
  const { fetch } = useFetch();
  const { data: subjects } = useSWR("admin/subjects", () => getSubjects({ fetch }));
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [role, setRole] = useState<AwhRole>("editor");
  const [name, setName] = useState("");
  const [studentId, setStudentId] = useState<string | null>(null);
  const [awh, setAwh] = useState<number | string>(0);
  const [moneyCoef, setMoneyCoef] = useState<number | string>("");
  const [state, setState] = useState<AwhState>("pending");

  useEffect(() => {
    if (row) { setName(row.student_id ? (formatFullName(row) || "") : (row.student_name ?? "")); setStudentId(row.student_id ?? null); setAwh(row.awh); setMoneyCoef(row.money_coef); setSubjectId(row.subject_id != null ? String(row.subject_id) : null); }
    else if (opened) { setRole("editor"); setName(""); setStudentId(null); setAwh(0); setMoneyCoef(""); setSubjectId(null); setState("pending"); }
  }, [row, opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={row ? "Изменить запись ЧРА" : "Добавить запись ЧРА"} centered>
      <Stack>
        <StaffSelect stacked label="Кому" value={name} studentId={studentId} onChange={(n, id) => { setName(n); setStudentId(id); }} />
        {/* A lecture brings its own subject; entries added by hand name theirs here. */}
        {row?.lecture_id
          ? <Text fz="sm" c="dimmed">{row.subject_title ?? "Без предмета"}, лекция {row.lecture_number} · {ROLE_LABEL[row.role] ?? row.role}</Text>
          : <Select label="Предмет" description="Без предмета запись не уйдёт на mipt.tech" placeholder="Не выбран" searchable clearable data={(subjects ?? []).map(s => ({ value: String(s.id), label: s.title }))} value={subjectId} onChange={setSubjectId} />}
        {!row && (
          <Group grow align="flex-start">
            <Select label="Роль" data={AWH_ROLES.map(r => ({ value: r, label: ROLE_LABEL[r] ?? r }))} value={role} onChange={v => setRole((v as AwhRole) ?? "editor")} allowDeselect={false} />
            <Select label="Статус" data={AWH_STATES.map(s => ({ value: s, label: STATE_LABEL[s] }))} value={state} onChange={v => setState((v as AwhState) ?? "pending")} allowDeselect={false} />
          </Group>
        )}
        <Group grow align="flex-start">
          <NumberInput label="ЧРА" value={awh} onChange={setAwh} min={0} step={0.5} decimalScale={2} />
          <NumberInput label="Денежный коэф." placeholder={row ? undefined : subjectId ? "По предмету" : "По роли"} value={moneyCoef} onChange={setMoneyCoef} min={0} max={100} allowDecimal={false} allowNegative={false} />
        </Group>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Отмена</Button>
          <Button onClick={() => onSubmit(row ? { awh: Number(awh) || 0, money_coef: Number(moneyCoef) || 0, ...(!row.lecture_id && { subject_id: subjectId ? Number(subjectId) : null }), ...(studentId ? { student_id: studentId, student_name: null } : { student_name: name.trim(), student_id: null }) } : { role, awh: Number(awh) || 0, subject_id: subjectId ? Number(subjectId) : null, ...(moneyCoef !== "" && { money_coef: Number(moneyCoef) }), state, ...(studentId ? { student_id: studentId } : { student_name: name.trim() }) })} loading={busy} disabled={(!studentId && !name.trim()) || (!!row && moneyCoef === "")}>{row ? "Сохранить" : "Добавить"}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function AwhTab() {
  const { fetch } = useFetch();
  const [applied, setApplied] = useState<{ status: AwhState | ""; type: "id" | "name" | "" }>({ status: "", type: "" });
  const { draft, patch, dirty } = useFilterDraft(applied);
  const [page, setPage] = useState(1);
  const apply = (f: typeof applied) => { setApplied(f); setPage(1); };
  const { status, type } = applied;
  // Pages of PAGE_SIZE, newest first: the whole history took long to load and render.
  const { data, error, isLoading, mutate } = useSWR(["admin/awh", status, type, page], () => getAwhSubmissions({ status: status || undefined, type: type || undefined, limit: PAGE_SIZE, start: (page - 1) * PAGE_SIZE, fetch }));
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AwhSubmission | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AwhSubmission | null>(null);
  const [modalBusy, setModalBusy] = useState(false);

  const runOp = async (fn: () => Promise<void>) => {
    setActionError(null); setModalBusy(true);
    try { await fn(); await mutate(); setEditing(null); setCreating(false); setDeleting(null); }
    catch (e: any) { setActionError(e?.message ?? String(e)); }
    finally { setModalBusy(false); setBusyId(null); }
  };

  if (error) return <ErrorDisplay error={error} />;

  return (
    <Stack>
      <Group align="flex-end">
        <FilterBar dirty={dirty} onApply={() => apply(draft)} onReset={() => apply({ status: "", type: "" })}>
        <Select w={200} label="Статус" placeholder="Все" clearable data={AWH_STATES.map(s => ({ value: s, label: STATE_LABEL[s] }))} value={draft.status || null} onChange={v => patch({ status: (v as AwhState) ?? "" })} />
        <Select w={220} label="Монтажёр указан" placeholder="Все" clearable data={[{ value: "id", label: "По студенту (student_id)" }, { value: "name", label: "Вручную (student_name)" }]} value={draft.type || null} onChange={v => patch({ type: (v as any) ?? "" })} />
        </FilterBar>
        {/* line-height = input height puts the text on the inputs' centre line */}
        <Button ml="auto" variant="light" onClick={() => setCreating(true)}>Добавить запись</Button>
      </Group>
      {actionError && <Alert color="red">{actionError}</Alert>}
      {isLoading || !data ? <Loading /> : (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr><Table.Th w={70}>Лекция</Table.Th><Table.Th>Предмет</Table.Th><Table.Th w={110}>Роль</Table.Th><Table.Th>Кому</Table.Th><Table.Th>ЧРА</Table.Th><Table.Th w={60}><Tooltip label="Денежный коэффициент" withArrow><span>ДК</span></Tooltip></Table.Th><Table.Th w={190}>Статус</Table.Th><Table.Th>Создано</Table.Th><Table.Th w={84} /></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map(row => (
              <Table.Tr key={row.id}>
                <Table.Td>{row.lecture_number ?? "—"}</Table.Td>
                <Table.Td><Text fz="sm">{row.subject_title ?? "—"}</Text></Table.Td>
                <Table.Td>{ROLE_LABEL[row.role] ?? row.role}</Table.Td>
                <Table.Td>{row.student_id ? <Tooltip label={row.student_archived ? `${row.student_id} · в архиве` : row.student_id} withArrow><span><PersonName archived={row.student_archived} hint={false}>{formatFullName(row) || row.student_id}</PersonName></span></Tooltip> : row.student_name ? <Group gap={6}><Text fz="sm">{row.student_name}</Text><Badge size="xs" variant="light" color="gray">вручную</Badge></Group> : <Text c="dimmed">—</Text>}</Table.Td>
                <Table.Td><Text fz="sm">{row.awh}</Text></Table.Td>
                <Table.Td><Text fz="sm">{row.money_coef}</Text></Table.Td>
                <Table.Td><StateControl state={row.state} busy={busyId === row.id} onChange={next => { setBusyId(row.id); runOp(() => updateAwhSubmission({ id: row.id, patch: { state: next }, fetch })); }} /></Table.Td>
                <Table.Td><Origin row={row} /></Table.Td>
                <Table.Td>
                  {row.state !== "success" && <Group gap={4} wrap="nowrap">
                    <ActionIcon variant="subtle" color="gray" disabled={busyId === row.id} onClick={() => setEditing(row)}><Pencil size={16} /></ActionIcon>
                    <ActionIcon variant="subtle" color="red" disabled={busyId === row.id} onClick={() => setDeleting(row)}><Trash2 size={16} /></ActionIcon>
                  </Group>}
                </Table.Td>
              </Table.Tr>
            ))}
            {data.length === 0 && <Table.Tr><Table.Td colSpan={9}><Text c="dimmed" ta="center" py="md">Записей нет</Text></Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      )}
      {data && <Pagination page={page} shown={data.length} hasNext={data.length >= PAGE_SIZE} onChange={setPage} />}
      <SubmissionModal opened={editing !== null} row={editing} busy={modalBusy} onClose={() => setEditing(null)} onSubmit={p => runOp(() => updateAwhSubmission({ id: editing!.id, patch: p, fetch }))} />
      <SubmissionModal opened={creating} busy={modalBusy} onClose={() => setCreating(false)} onSubmit={p => runOp(() => createAwhSubmission({ payload: p, fetch }))} />
      <Modal opened={deleting !== null} onClose={() => setDeleting(null)} title="Удалить запись ЧРА?" centered>
        <Text size="sm">Запись будет удалена безвозвратно.</Text>
        <Group justify="flex-end" mt="md"><Button variant="default" onClick={() => setDeleting(null)}>Отмена</Button><Button color="red" loading={modalBusy} onClick={() => runOp(() => deleteAwhSubmission({ id: deleting!.id, fetch }))}>Удалить</Button></Group>
      </Modal>
    </Stack>
  );
}
